import { Link, router } from "@inertiajs/react";
import { CheckCircle2, Download, Undo2, DollarSign, BookOpen, Clock4 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import AwardStyleBadge from "@/components/schools/rewards/AwardStyleBadge";
import { AWARD_STYLES, prizeDescription } from "@/lib/prizes";
import { timeAgo } from "@/lib/timeAgo";
import { cn } from "@/lib/utils";

// Pieces shared by the school prize report (Campaigns/Disbursements) and
// a classroom's prize tracker (PrizeTrackerCard), so handing out a prize
// looks and works the same for admins and teachers.

/** A student prize's card: icon, name, style badge, how it's won, and a stat. */
export function PrizeCard({ prize, description, stat, children }) {
  const styled = prize.award_style && prize.award_style !== "unlock";
  const Icon = styled ? AWARD_STYLES[prize.award_style].icon : prize.kind === "money" ? DollarSign : BookOpen;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="flex items-center gap-2">
                <Icon className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                {prize.prize}
              </CardTitle>
              <AwardStyleBadge style={prize.award_style} />
            </div>
            <CardDescription className="mt-1">{description || prizeDescription(prize)}</CardDescription>
          </div>
          {stat && <div className="shrink-0 sm:text-right">{stat}</div>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

/** "3 / 8 handed out" — nothing until someone has earned the prize. */
export function HandedOutStat({ handedOut, earned }) {
  if (!earned) return null;
  return (
    <>
      <p className="font-display text-2xl font-semibold leading-none font-tabular">
        {handedOut}<span className="text-base text-muted-foreground"> / {earned}</span>
      </p>
      <p className="mt-1 text-xs text-muted-foreground">handed out</p>
    </>
  );
}

/** "3 waiting" or "All handed out", readable on cream. */
export function WaitingPill({ count }) {
  return count === 0 ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-semibold text-success">
      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
      All handed out
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-warning/15 px-2.5 py-0.5 text-xs font-semibold text-warning-foreground">
      <Clock4 className="h-3.5 w-3.5" aria-hidden="true" />
      {count} waiting
    </span>
  );
}

export function StudentLink({ name, href, className }) {
  const classes = cn("text-sm text-foreground", href && "hover:text-primary transition-colors", className);
  return href ? <Link href={href} className={classes}>{name}</Link> : <span className={classes}>{name}</span>;
}

/**
 * One student's prize: waiting (with "Mark handed out") or handed out
 * (with who and when, and Undo). Certificates download when there is one.
 *
 * achievement: { student_name, student_path, achieved_at, disbursed, disbursed_at,
 *                disbursed_by, disburse_path, undisburse_path, certificate_path }
 */
export function HandOutRow({ achievement, prizeName, detail, extraActions }) {
  const confirm = useConfirm();
  const handedOut = achievement.disbursed ?? !!achievement.disbursed_at;

  const undo = async () => {
    const ok = await confirm({
      title: `Mark ${achievement.student_name}'s ${prizeName} as not handed out?`,
      body: "It goes back on the list of prizes still to hand out.",
      confirmLabel: "Mark not handed out",
    });
    if (ok) router.patch(achievement.undisburse_path, {}, { preserveScroll: true });
  };

  const subline = detail ?? (handedOut
    ? `Handed out ${timeAgo(achievement.disbursed_at)}${achievement.disbursed_by ? ` by ${achievement.disbursed_by}` : ""}`
    : achievement.achieved_at && `Earned ${timeAgo(achievement.achieved_at)}`);

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex min-w-0 flex-1 items-start gap-2">
        {handedOut && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-label="Handed out" />}
        <div className="min-w-0">
          <StudentLink name={achievement.student_name} href={achievement.student_path} className="block font-medium" />
          {subline && <p className="text-xs text-muted-foreground">{subline}</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {achievement.certificate_path && (
          <Button size="sm" variant="outline" asChild>
            <a href={achievement.certificate_path}>
              <Download className="h-4 w-4" />
              Certificate
            </a>
          </Button>
        )}
        {handedOut ? (
          achievement.undisburse_path && (
            <Button size="sm" variant="ghost" onClick={undo} className="text-muted-foreground">
              <Undo2 className="h-4 w-4" />
              Undo
            </Button>
          )
        ) : (
          <Button size="sm" variant="outline" onClick={() => router.patch(achievement.disburse_path, {}, { preserveScroll: true })}>
            <CheckCircle2 className="h-4 w-4" />
            Mark handed out
          </Button>
        )}
        {extraActions}
      </div>
    </li>
  );
}

/** Hand one prize out to everyone in a class who's still waiting. */
export function HandOutAllButton({ path, count, prizeName }) {
  const confirm = useConfirm();
  if (!path || count < 2) return null;

  const handOut = async () => {
    const ok = await confirm({
      title: `Mark ${prizeName} handed out for all ${count} students?`,
      body: "Use this once the whole class has their prize. You can undo any one of them afterwards.",
      confirmLabel: `Mark all ${count} handed out`,
    });
    if (ok) router.patch(path, {}, { preserveScroll: true });
  };

  return (
    <Button size="sm" variant="outline" onClick={handOut}>
      <CheckCircle2 className="h-4 w-4" />
      Mark all {count} handed out
    </Button>
  );
}

/** A collapsed list of the students who've had their prize, with Undo. */
export function HandedOutList({ achievements, prizeName }) {
  if (!achievements?.length) return null;
  return (
    <details className="group border-t border-border">
      <summary className="cursor-pointer list-none px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground">
        <span className="group-open:hidden">Show {achievements.length} handed out</span>
        <span className="hidden group-open:inline">Hide handed out</span>
      </summary>
      <ul className="divide-y divide-border/60 border-t border-border/60">
        {achievements.map((a) => <HandOutRow key={a.id} achievement={a} prizeName={prizeName} />)}
      </ul>
    </details>
  );
}
